/**
 * Suggestions automatiques de blocs conclusions basées sur la distribution
 * des mesures (code couleur) de la campagne en cours.
 *
 * Regles (derivees du code couleur Renaud) :
 *   Tout vert (<20%)         : fiabilite non remise en cause
 *   Orange max (<30%)        : fiabilite ok avec reserve + periodicite 2-3 ans
 *   Mesures rouges (30-39%)  : fiabilite remise en cause + macrographies
 *   Mesures rouge sombre(>40%): fiabilite remise en cause (urgent)
 *
 * Le bloc bilan-stats est toujours suggere en premier.
 */

import type { OfflineMesure } from "@/core/offline/db";

export interface ConclusionSuggestion {
  blockId: string;
  reason: string;
}

export interface MesureDistribution {
  total: number;
  green: number;  // 0-19%
  orange: number; // 20-29%
  red: number;    // 30-39%
  dark: number;   // >=40%
  withValue: number; // mesures avec pourcentageCorrosion non nul
}

export function computeDistribution(mesures: OfflineMesure[]): MesureDistribution {
  let green = 0, orange = 0, red = 0, dark = 0, withValue = 0;

  for (const m of mesures) {
    const pct = m.pourcentageCorrosion;
    if (pct == null) continue;
    withValue++;
    if (pct < 20) green++;
    else if (pct < 30) orange++;
    else if (pct < 40) red++;
    else dark++;
  }

  return { total: mesures.length, green, orange, red, dark, withValue };
}

export function computeSuggestions(dist: MesureDistribution): ConclusionSuggestion[] {
  const suggestions: ConclusionSuggestion[] = [];

  if (dist.withValue === 0) return suggestions;

  // Toujours suggerer le bilan statistique en premier
  suggestions.push({
    blockId: "lib-conclu-bilan-stats",
    reason: `${dist.withValue} mesures relevees`,
  });

  if (dist.dark > 0) {
    // Mesures rouge sombre (>40%) : remplacement urgent
    suggestions.push({
      blockId: "lib-conclu-fiabilite-remise-cause",
      reason: `${dist.dark} mesure(s) rouge sombre (>40%)`,
    });
    suggestions.push({
      blockId: "lib-conclu-macrographies-preconisation",
      reason: "Presence de corrosion severe (>40%)",
    });
  } else if (dist.red > 0) {
    // Mesures rouges (30-39%) : remise en cause + macrographies
    suggestions.push({
      blockId: "lib-conclu-fiabilite-remise-cause",
      reason: `${dist.red} mesure(s) rouge (30-39%)`,
    });
    suggestions.push({
      blockId: "lib-conclu-macrographies-preconisation",
      reason: "Presence de pertes superieures a 30%",
    });
    suggestions.push({
      blockId: "lib-conclu-periodicite-2-3-ans",
      reason: "Suivi recommande",
    });
  } else if (dist.orange > 0) {
    // Mesures orange (20-29%) : ok avec reserve + periodicite
    suggestions.push({
      blockId: "lib-conclu-fiabilite-ok-reserve",
      reason: `${dist.orange} mesure(s) orange (20-29%)`,
    });
    suggestions.push({
      blockId: "lib-conclu-periodicite-2-3-ans",
      reason: "Amorce de corrosion detectee",
    });
  } else {
    // Tout vert : fiabilite non remise en cause
    suggestions.push({
      blockId: "lib-conclu-fiabilite-ok",
      reason: `${dist.green} mesure(s) dans les normes (<20%)`,
    });
  }

  return suggestions;
}
